import { defineStore } from "pinia";
import type { User } from "oidc-client-ts";
import { computed, nextTick, ref } from "vue";
import { useOpenIdAuthenticationStore } from "@/stores/AppStartUp/OpenIdAuthenticationStore";
import { RoleName } from "@/constants";
import { useAccessTokenStore } from "@/stores/AppStartUp/AccessTokenStore";

export const useAuthUserStore = defineStore("authUserStore", () => {
    const authUser = ref<User | null>();
    const authUserIsInitialized = ref<boolean>(false);
    const initializationWasExecuted = ref<boolean>(false);
    const accessTokenStore = useAccessTokenStore();

    const { getUser } = useOpenIdAuthenticationStore();

    const initialize = async () => {
        try {
            authUser.value = await getUser();

            if (authUser.value) {
                authUserIsInitialized.value = true;
            }

            await nextTick();

            await accessTokenStore.saveAccessToken(authUser.value?.access_token || "");
            accessTokenStore.setExpireDate(authUser.value?.expires_at as number);
        } catch (error) {
            console.error("AuthUserStore: Failed to initialize auth user", error);
            authUser.value = null;
        } finally {
            initializationWasExecuted.value = true;
        }
    };

    const authUserIsLoaded = computed(() => authUserIsInitialized.value && !!authUser.value);

    const profile = computed(() => authUser.value?.profile);
    const accessToken = computed(() => authUser.value?.access_token);
    const roles = computed(() => authUser.value?.profile?.groups as string[]);

    const isAmaFreightUser = computed(() => roles.value?.includes(RoleName.AmaFreight));
    const isSCSUser = computed(() => roles.value?.includes(RoleName.SCS));
    const isAdminUser = computed(() => roles.value?.includes(RoleName.Admin));
    const isSuperAdminUser = computed(() => roles.value?.includes(RoleName.SuperAdmin));

    const reset = async () => {
        authUser.value = undefined;

        await nextTick();
    };

    return {
        initialize,
        authUser,
        authUserIsLoaded,
        profile,
        accessToken,
        roles,
        isAmaFreightUser,
        isSCSUser,
        isAdminUser,
        isSuperAdminUser,
        initializationWasExecuted,
        reset
    };
});
